import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import api from '@/services/api'

const MODULES = {
  MY_OFFICE: 'my_office',
  OPERATIONS: 'operations',
  FINANCE: 'finance',
  SALES: 'sales',
  CRM: 'crm',
  ANALYTICS: 'analytics',
  LOGISTICS: 'logistics',
  NOTIFICATIONS_CONFIG: 'notifications_config',
  MASTER_DATA: 'master_data',
  AUTOMATION: 'automation',
  INTEGRATION: 'integration',
  SYSTEM: 'system',
  MOLOLINK: 'mololink',
  ADMIN: 'admin'
} as const

type ModuleKey = keyof typeof MODULES
type ModuleValue = typeof MODULES[ModuleKey]
type Action = 'read' | 'create' | 'update' | 'delete'

interface PermissionData {
  role: string
  permissions: Record<string, Action[]>
  modulePermissions?: Record<string, Action[]>
}

const FALLBACK_PERMISSIONS: Record<string, Record<string, Action[]>> = {
  admin: {
    [MODULES.MY_OFFICE]: ['read', 'create', 'update', 'delete'],
    [MODULES.OPERATIONS]: ['read', 'create', 'update', 'delete'],
    [MODULES.FINANCE]: ['read', 'create', 'update', 'delete'],
    [MODULES.SALES]: ['read', 'create', 'update', 'delete'],
    [MODULES.CRM]: ['read', 'create', 'update', 'delete'],
    [MODULES.ANALYTICS]: ['read'],
    [MODULES.LOGISTICS]: ['read', 'create', 'update', 'delete'],
    [MODULES.NOTIFICATIONS_CONFIG]: ['read', 'create', 'update', 'delete'],
    [MODULES.MASTER_DATA]: ['read', 'create', 'update', 'delete'],
    [MODULES.AUTOMATION]: ['read', 'create', 'update', 'delete'],
    [MODULES.INTEGRATION]: ['read', 'create', 'update', 'delete'],
    [MODULES.SYSTEM]: ['read', 'create', 'update', 'delete'],
    [MODULES.MOLOLINK]: ['read', 'create', 'update', 'delete'],
    [MODULES.ADMIN]: ['read', 'create', 'update', 'delete']
  },
  super_admin: {
    [MODULES.MY_OFFICE]: ['read', 'create', 'update', 'delete'],
    [MODULES.OPERATIONS]: ['read', 'create', 'update', 'delete'],
    [MODULES.FINANCE]: ['read', 'create', 'update', 'delete'],
    [MODULES.SALES]: ['read', 'create', 'update', 'delete'],
    [MODULES.CRM]: ['read', 'create', 'update', 'delete'],
    [MODULES.ANALYTICS]: ['read'],
    [MODULES.LOGISTICS]: ['read', 'create', 'update', 'delete'],
    [MODULES.NOTIFICATIONS_CONFIG]: ['read', 'create', 'update', 'delete'],
    [MODULES.MASTER_DATA]: ['read', 'create', 'update', 'delete'],
    [MODULES.AUTOMATION]: ['read', 'create', 'update', 'delete'],
    [MODULES.INTEGRATION]: ['read', 'create', 'update', 'delete'],
    [MODULES.SYSTEM]: ['read', 'create', 'update', 'delete'],
    [MODULES.MOLOLINK]: ['read', 'create', 'update', 'delete'],
    [MODULES.ADMIN]: ['read', 'create', 'update', 'delete']
  },
  manager: {
    [MODULES.MY_OFFICE]: ['read', 'create', 'update', 'delete'],
    [MODULES.OPERATIONS]: ['read', 'create', 'update', 'delete'],
    [MODULES.FINANCE]: ['read', 'create', 'update'],
    [MODULES.SALES]: ['read', 'create', 'update'],
    [MODULES.CRM]: ['read', 'create', 'update', 'delete'],
    [MODULES.ANALYTICS]: ['read'],
    [MODULES.LOGISTICS]: ['read', 'create', 'update', 'delete'],
    [MODULES.MASTER_DATA]: ['read', 'create', 'update'],
    [MODULES.INTEGRATION]: ['read'],
    [MODULES.MOLOLINK]: ['read', 'create', 'update']
  },
  operator: {
    [MODULES.MY_OFFICE]: ['read', 'create', 'update'],
    [MODULES.OPERATIONS]: ['read', 'create', 'update'],
    [MODULES.CRM]: ['read', 'create', 'update'],
    [MODULES.LOGISTICS]: ['read'],
    [MODULES.MASTER_DATA]: ['read'],
    [MODULES.MOLOLINK]: ['read', 'create', 'update']
  },
  finance: {
    [MODULES.MY_OFFICE]: ['read', 'create', 'update'],
    [MODULES.OPERATIONS]: ['read'],
    [MODULES.FINANCE]: ['read', 'create', 'update', 'delete'],
    [MODULES.ANALYTICS]: ['read'],
    [MODULES.LOGISTICS]: ['read'],
    [MODULES.MASTER_DATA]: ['read'],
    [MODULES.MOLOLINK]: ['read']
  },
  user: {
    [MODULES.MY_OFFICE]: ['read', 'create', 'update'],
    [MODULES.OPERATIONS]: ['read', 'create', 'update'],
    [MODULES.MOLOLINK]: ['read', 'create', 'update']
  },
  viewer: {
    [MODULES.MY_OFFICE]: ['read'],
    [MODULES.OPERATIONS]: ['read'],
    [MODULES.MOLOLINK]: ['read']
  }
}

export function usePermissions() {
  const { user } = useAuth()
  const [permissionData, setPermissionData] = useState<PermissionData | null>(null)
  const [loaded, setLoaded] = useState(false)

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await api.get('/auth/permissions')
      if (res?.success && res.data) {
        setPermissionData(res.data)
      }
    } catch {
      const role = user?.role || 'viewer'
      const perms = FALLBACK_PERMISSIONS[role] || FALLBACK_PERMISSIONS.viewer
      setPermissionData({ role, permissions: perms })
    } finally {
      setLoaded(true)
    }
  }, [user?.role])

  useEffect(() => {
    if (user) {
      fetchPermissions()
    }
  }, [user, fetchPermissions])

  const hasModuleAccess = useCallback((module: ModuleValue): boolean => {
    // Preferred: server-emitted module-keyed permissions (added by
    // getUserPermissionsSummary via MODULE_TO_RESOURCES). Honors
    // user_permission_overrides because it's computed from the merged perm set.
    const modulePerms = permissionData?.modulePermissions?.[module]
    if (modulePerms && modulePerms.length > 0) {
      return true
    }
    // Compat path: some older callers/tests pass resource-keyed maps.
    if (permissionData?.permissions[module] && permissionData.permissions[module].length > 0) {
      return true
    }
    // Last resort: role-based static fallback (used when server is unreachable
    // OR when an old server build hasn't shipped modulePermissions yet).
    if (permissionData?.modulePermissions) {
      // Server responded with module shape but this module isn't in it → deny.
      return false
    }
    const role = permissionData?.role || user?.role || 'viewer'
    const perms = FALLBACK_PERMISSIONS[role] || FALLBACK_PERMISSIONS.viewer
    return !!(perms[module] && perms[module].length > 0)
  }, [permissionData, user?.role])

  const hasPermission = useCallback((module: ModuleValue, action: Action): boolean => {
    if (permissionData?.modulePermissions?.[module]) {
      return permissionData.modulePermissions[module].includes(action)
    }
    if (!permissionData) {
      const role = user?.role || 'viewer'
      const perms = FALLBACK_PERMISSIONS[role] || FALLBACK_PERMISSIONS.viewer
      return !!(perms[module] && perms[module].includes(action))
    }
    const perms = permissionData.permissions[module]
    return !!(perms && perms.includes(action))
  }, [permissionData, user?.role])

  const canWrite = useCallback((module: ModuleValue): boolean => {
    return hasPermission(module, 'create') || hasPermission(module, 'update')
  }, [hasPermission])

  const canDelete = useCallback((module: ModuleValue): boolean => {
    return hasPermission(module, 'delete')
  }, [hasPermission])

  const isAdmin = useMemo(() => {
    const role = permissionData?.role || user?.role || ''
    return role === 'admin' || role === 'super_admin'
  }, [permissionData?.role, user?.role])

  return {
    MODULES,
    hasModuleAccess,
    hasPermission,
    canWrite,
    canDelete,
    isAdmin,
    role: permissionData?.role || user?.role || 'viewer',
    loaded
  }
}
